import { API_URL } from "./../../constants";
import { currencyFormatter } from "./../../functions";
import { useCustom, useGetIdentity, useNavigation } from "@refinedev/core";
import { Row, Col, Card, Typography, Button, Progress } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
    PlusOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    DollarOutlined,
    UsergroupAddOutlined,
    ArrowRightOutlined,
    BankOutlined,
    DownloadOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import { RecentInvoices } from "components";
import { IIdentity } from "interfaces";
import styles from "./dashboard.module.css";

const { Text, Title } = Typography;

export const DashboardPage: React.FC = () => {
    const { t } = useTranslation();
    const { push } = useNavigation();
    const { data: user } = useGetIdentity<IIdentity>();

    const url = `${API_URL}/metrics`;
    const { data } = useCustom<{ data: any }>({ url, method: "get" });
    const metrics: any = data?.data;

    const canAccess = ["admin", "owner"].includes(localStorage.getItem("role") ?? "");

    const firstName = user?.name?.split(" ")?.[0] ?? "";
    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";

    const totalInvoices = Number(metrics?.invoices ?? 0);
    const paidInvoices = Number(metrics?.invoices_paid ?? 0);
    const paymentRate = totalInvoices > 0 ? Math.round((paidInvoices / totalInvoices) * 100) : 0;

    const onboardingIncomplete =
        metrics &&
        canAccess &&
        (Number(metrics?.customers) <= 0 ||
            Number(metrics?.companyaccounts) <= 0 ||
            Number(metrics?.invoices) <= 0 ||
            Number(metrics?.users) <= 1);

    const onboardingSteps = [
        { done: Number(metrics?.customers) > 0, label: "Cadastrar clientes", link: "/customers" },
        { done: Number(metrics?.companyaccounts) > 0, label: "Cadastrar conta bancária", link: "/companyAccounts" },
        { done: Number(metrics?.invoices) > 0, label: "Criar uma cobrança", link: "/invoices/create" },
        { done: Number(metrics?.users) > 1, label: "Cadastrar funcionários", link: "/users" },
    ];

    const stats = [
        {
            tone: "blue",
            icon: <TeamOutlined />,
            label: "Clientes cadastrados",
            value: Number(metrics?.customers ?? 0).toLocaleString("pt-BR"),
        },
        {
            tone: "green",
            icon: <CheckCircleOutlined />,
            label: "Cobranças pagas",
            value: paidInvoices.toLocaleString("pt-BR"),
        },
        {
            tone: "violet",
            icon: <DollarOutlined />,
            label: "Valor transacionado",
            value: currencyFormatter(metrics?.invoices_paid_total ?? 0),
        },
        {
            tone: "amber",
            icon: <UsergroupAddOutlined />,
            label: "Funcionários",
            value: Number(metrics?.users ?? 0).toLocaleString("pt-BR"),
        },
    ];

    const quickActions = [
        { icon: <FileTextOutlined />, label: "Nova cobrança", desc: "Gerar PIX ou boleto", link: "/invoices/create", tone: "blue" },
        { icon: <DownloadOutlined />, label: "Novo recebedor", desc: "Cadastrar quem recebe", link: "/customers/create", tone: "green" },
        { icon: <BankOutlined />, label: "Nova conta", desc: "Conta bancária da empresa", link: "/companyAccounts/create", tone: "violet" },
        { icon: <UsergroupAddOutlined />, label: "Novo funcionário", desc: "Dar acesso à equipe", link: "/users/create", tone: "amber" },
    ];

    return (
        <div className={styles.dashboardContainer}>
            {/* Hero banner */}
            <div className={styles.hero} data-tour="dashboard-hero">
                <div className={styles.heroGlow} />
                <div className={styles.heroContent}>
                    <div>
                        <span className={styles.heroEyebrow}>{greeting}</span>
                        <Title level={2} className={styles.heroTitle}>
                            {firstName ? `Olá, ${firstName} 👋` : "Bem-vindo ao Split Fácil 👋"}
                        </Title>
                        <Text className={styles.heroSubtitle}>
                            Acompanhe suas cobranças e o repasse automático para os recebedores.
                        </Text>
                    </div>
                    {canAccess && (
                        <Button
                            size="large"
                            icon={<PlusOutlined />}
                            className={styles.heroButton}
                            onClick={() => push("/invoices/create")}
                        >
                            Criar Cobrança
                        </Button>
                    )}
                </div>
            </div>

            {/* KPIs */}
            <Row gutter={[20, 20]} className={styles.statsRow}>
                {stats.map((s) => (
                    <Col xl={6} lg={6} md={12} sm={12} xs={24} key={s.label}>
                        <Card bordered={false} className={styles.statCard} data-tone={s.tone}>
                            <div className={styles.statIcon} data-tone={s.tone}>
                                {s.icon}
                            </div>
                            <div className={styles.statBody}>
                                <span className={styles.statLabel}>{s.label}</span>
                                <span className={styles.statValue}>{s.value}</span>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row gutter={[20, 20]} className={styles.statsRow}>
                {/* Recentes */}
                <Col xl={15} lg={14} md={24} sm={24} xs={24} data-tour="dashboard-recent">
                    <Card
                        bordered={false}
                        className={styles.recentInvoicesCard}
                        title={
                            <div className={styles.recentHeader}>
                                <span className={styles.cardTitle}>
                                    {t("dashboard.recentInvoices.title")}
                                </span>
                                <Link to="/invoices" className={styles.viewAllLink}>
                                    Ver todas <ArrowRightOutlined />
                                </Link>
                            </div>
                        }
                    >
                        <RecentInvoices />
                    </Card>
                </Col>

                {/* Coluna direita: taxa de pagamento + onboarding/ações */}
                <Col xl={9} lg={10} md={24} sm={24} xs={24} data-tour="dashboard-quick-actions">
                    <Card bordered={false} className={styles.rateCard}>
                        <span className={styles.cardTitle}>Taxa de pagamento</span>
                        <div className={styles.rateBody}>
                            <Progress
                                type="dashboard"
                                percent={paymentRate}
                                strokeColor={{ "0%": "#3b82f6", "100%": "#2563eb" }}
                                strokeWidth={10}
                                size={150}
                                format={(p) => (
                                    <span className={styles.rateValue}>{p}%</span>
                                )}
                            />
                            <Text className={styles.rateCaption}>
                                {paidInvoices} de {totalInvoices} cobranças pagas
                            </Text>
                        </div>
                    </Card>

                    {onboardingIncomplete ? (
                        <Card
                            bordered={false}
                            className={styles.sideCard}
                            title={<span className={styles.cardTitle}>Primeiros passos</span>}
                        >
                            <ul className={styles.onboardingList}>
                                {onboardingSteps.map((step) => (
                                    <li
                                        key={step.label}
                                        className={`${styles.onboardingItem} ${step.done ? styles.onboardingDone : ""}`}
                                    >
                                        <span className={styles.onboardingText}>
                                            <span className={`${styles.checkDot} ${step.done ? styles.checkDotDone : ""}`}>
                                                {step.done ? <CheckCircleOutlined /> : null}
                                            </span>
                                            {step.label}
                                        </span>
                                        {!step.done && (
                                            <Link to={step.link} className={styles.onboardingLink}>
                                                <ArrowRightOutlined />
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    ) : (
                        <Card
                            bordered={false}
                            className={styles.sideCard}
                            title={<span className={styles.cardTitle}>Ações rápidas</span>}
                        >
                            <div className={styles.quickGrid}>
                                {quickActions.map((a) => (
                                    <Link to={a.link} key={a.label} className={styles.quickItem}>
                                        <span className={styles.quickIcon} data-tone={a.tone}>
                                            {a.icon}
                                        </span>
                                        <span className={styles.quickText}>
                                            <span className={styles.quickLabel}>{a.label}</span>
                                            <span className={styles.quickDesc}>{a.desc}</span>
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};
